<template>
  <EcxPopover position="bottom" align="start" width="w-72" content-class="!p-1" @open="handleOpen">
    <template #trigger>
      <button
        type="button"
        class="flex max-w-xs items-center gap-2 rounded-lg bg-royal-25 p-2 text-royal-500"
      >
        <OfficeBuildingIcon class="w-4 h-4 flex-shrink-0" />
        <span class="min-w-0 truncate text-xs">{{ currentCompanyName }}</span>
        <ChevronDownIcon class="w-5 h-5 flex-shrink-0" />
      </button>
    </template>

    <template #header>
      <span class="text-xs font-medium uppercase text-grey-600">
        {{ t('corp.company_switcher.title') }}
      </span>
    </template>

    <template #default="{ close }">
      <div v-if="isLoading" class="flex justify-center py-6">
        <EcxSpinner size="sm" />
      </div>
      <p v-else-if="error" class="py-6 text-center text-sm text-grey-600">
        {{ error }}
      </p>
      <ul v-else class="flex flex-col gap-1">
        <li v-for="company in companies" :key="company.id">
          <CompanySwitcherRow
            :company="company"
            :is-current="company.id === currentAccountId"
            :disabled="isSwitching"
            @select="handleSelect($event, close)"
          />
        </li>
      </ul>
    </template>
  </EcxPopover>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { EcxPopover, EcxSpinner } from '@ecx/ui';
import { OfficeBuildingIcon, ChevronDownIcon } from '@ecx/ui/icons';

import CompanySwitcherRow from './CompanySwitcherRow.vue';
import { useCompanyStore } from '@/stores/company';
import { useAuthStore } from '@/stores/auth';
import { useUserStore } from '@/stores/user';

const { t } = useI18n();

const companyStore = useCompanyStore();
const authStore = useAuthStore();
const userStore = useUserStore();

const companies = computed(() => companyStore.companies);
const isLoading = computed(() => companyStore.isLoading);
const error = computed(() => companyStore.error);
const currentAccountId = computed(() => authStore.accountId);

const isSwitching = ref(false);

const currentCompanyName = computed(() => companyStore.companyName || userStore.userName);

const handleOpen = () => {
  companyStore.fetchCompanies();
};

const handleSelect = async (companyId: string, close: () => void) => {
  if (companyId === currentAccountId.value) {
    close();
    return;
  }
  isSwitching.value = true;
  await companyStore.switchCompany(companyId);
  isSwitching.value = false;
};
</script>
